import { useQuery } from '@apollo/client';
import React from 'react';
import { Page } from '../../layout';
import SearchControls from '../../shared/components/SearchControls';
import PaginationControls from '../../shared/components/PaginationControls';
import GET_ORG_UNITS from '../queries/unitSearchQuery';
import OrgUnitInfoCard from './OrgUnitInfoCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUnitSearchTarget } from '../../shared/services/getUnitSearchPageUrlService';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';
import ApolloError from '../../shared/components/ApolloError';
import Nbsp from '../../shared/components/Nbsp';
import MultipleSkeletons from '../../shared/components/skeleton/MultipleSkeletons';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import useSearchState from '../../organizationSearch/hooks/useSearchState';

const UnitSearchPage = () => {
  const pageSize = 20;

  const location = useLocation();
  const currentPageURL = location.pathname;

  const { currentPage, searchText, searchChar, searchOHT, setSearchState } = useSearchState();

  const redirectedURL = getUnitSearchTarget(currentPageURL);

  const navigate = useNavigate();

  const {
    data: searchResult,
    loading,
    error
  } = useQuery(GET_ORG_UNITS, {
    variables: {
      character: searchChar,
      filter: searchText,
      ohts: searchOHT,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize
    }
  });

  const totalPages = searchResult ? Math.ceil(searchResult.mddapOrgUnitsCount / pageSize) : 0;

  const { breadcrumbPathItems, pageTitle } = buildPageMetadata(currentPageURL);

  const handleSelectOrgUnit = orgUnitId => {
    navigate(redirectedURL + '/' + orgUnitId);
  };

  if (error) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle}>
      <Breadcrumb pathItems={breadcrumbPathItems} />

      <div className='item-search'>
        <SearchControls
          setSearchState={setSearchState}
          selectedSearchChar={searchChar}
          selectedSearchOHTs={searchOHT}
          selectedSearchText={searchText}
          searchTextPlaceholder='Search by Name or FEI #'
        />
        <div>
          <div className='item-search__results-count'>
            <p>
              Results:
              <Nbsp />
              <SkeletonWrapper
                show={loading}
                renderComponent={() => searchResult.mddapOrgUnitsCount}
              />
            </p>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onClick={page => setSearchState({ currentPage: page })}
            />
          </div>
          <div className='search-result' data-testid='search-result'>
            <MultipleSkeletons count={2} show={loading} />
            {!loading && searchResult.mddapOrgUnits.length === 0 && (
              <p>Search above to see results</p>
            )}
            {!loading && searchResult.mddapOrgUnits.length > 0 && (
              <>
                {searchResult.mddapOrgUnits.map(result => (
                  <OrgUnitInfoCard key={result.Id} onClick={handleSelectOrgUnit} orgUnit={result} />
                ))}
                <div className='item-search__pagination-bottom'>
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onClick={page => setSearchState({ currentPage: page })}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

const buildPageMetadata = currentPageURL => {
  const breadcrumb = [];
  const pageTitle = currentPageURL.includes('/unit-search') ? 'Unit Search' : 'Unit Selection';

  if (currentPageURL.includes('/data-reports')) {
    breadcrumb.push({ name: 'Data Reports', url: '/data-reports' });
  }

  if (currentPageURL.includes('/industry-reports')) {
    breadcrumb.push({ name: 'Industry Reports', url: '/data-reports/industry-reports' });
  }

  if (currentPageURL.includes('/appraisal-comparison')) {
    breadcrumb.push({ name: 'Appraisal Comparison', url: currentPageURL });
  }

  if (currentPageURL.includes('/organization-reports')) {
    breadcrumb.push({ name: 'Organization Reports', url: '/data-reports/organization-reports' });
  }

  if (currentPageURL.includes('/report-management')) {
    breadcrumb.push({ name: 'Report Management', url: currentPageURL });
  }

  if (currentPageURL.includes('/check-points')) {
    breadcrumb.push({ name: 'Check Points', url: currentPageURL });
  }

  breadcrumb.push({ name: pageTitle, url: '' });

  return { breadcrumbPathItems: breadcrumb, pageTitle };
};

export default UnitSearchPage;
